import { useEffect } from "react";
import { X, FileText, Info, ExternalLink } from "lucide-react";
import type { ReleaseNotesExport } from "../api/client";
import { titleCase } from "../lib/utils";

interface ReleaseNotesViewModalProps {
  releaseNotes: ReleaseNotesExport;
  onClose: () => void;
}

export default function ReleaseNotesViewModal({
  releaseNotes,
  onClose,
}: ReleaseNotesViewModalProps) {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const hasDeploymentSections =
    releaseNotes.sections && Object.keys(releaseNotes.sections).length > 0;
  const hasInformationalSections =
    releaseNotes.informational &&
    Object.keys(releaseNotes.informational).length > 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-brand-600 rounded-t-lg">
          <div>
            <h2 className="text-lg font-semibold text-white">Release Notes</h2>
            <p className="text-brand-200 text-sm">
              {titleCase(releaseNotes.service)} - {releaseNotes.environment} -{" "}
              {releaseNotes.version}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/80 hover:text-white rounded-md hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {releaseNotes.title && (
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {releaseNotes.title}
            </h3>
          )}

          {/* Deployment Sections */}
          {hasDeploymentSections && (
            <div className="space-y-8">
              {Object.entries(releaseNotes.sections).map(
                ([sectionName, items]) => (
                  <SectionRenderer
                    key={sectionName}
                    sectionName={sectionName}
                    items={items}
                    icon={<FileText className="h-5 w-5 text-brand-600" />}
                  />
                ),
              )}
            </div>
          )}

          {/* Informational Sections */}
          {hasInformationalSections && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-6">
                <Info className="h-5 w-5 text-blue-600" />
                <h4 className="text-lg font-medium text-gray-900">
                  Additional Information
                </h4>
              </div>
              <div className="space-y-8">
                {Object.entries(releaseNotes.informational).map(
                  ([sectionName, items]) => (
                    <SectionRenderer
                      key={sectionName}
                      sectionName={sectionName}
                      items={items}
                      icon={<Info className="h-5 w-5 text-blue-600" />}
                      isInformational
                    />
                  ),
                )}
              </div>
            </div>
          )}

          {/* No content */}
          {!hasDeploymentSections && !hasInformationalSections && (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p>No release notes content available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Section renderer component
interface SectionRendererProps {
  sectionName: string;
  items: Record<string, unknown>[];
  icon: React.ReactNode;
  isInformational?: boolean;
}

function SectionRenderer({
  sectionName,
  items,
  icon,
  isInformational,
}: SectionRendererProps) {
  if (!items || items.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <h4 className="text-lg font-medium text-gray-900">
            {formatSectionName(sectionName)}
          </h4>
        </div>
        <p className="text-gray-500 italic ml-7">No items in this section</p>
      </div>
    );
  }

  // Determine the best rendering strategy based on the data structure
  const renderStrategy = determineRenderStrategy(sectionName, items);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h4 className="text-lg font-medium text-gray-900">
          {formatSectionName(sectionName)}
        </h4>
        <span className="text-sm text-gray-500">({items.length})</span>
      </div>
      <div className={isInformational ? "ml-7" : ""}>
        {renderStrategy === "container-table" && (
          <ContainerTable items={items} />
        )}
        {renderStrategy === "url-list" && <UrlList items={items} />}
        {renderStrategy === "key-value-table" && (
          <KeyValueTable items={items} />
        )}
        {renderStrategy === "simple-list" && <SimpleList items={items} />}
        {renderStrategy === "generic" && <GenericRenderer items={items} />}
      </div>
    </div>
  );
}

// Format section name for display
function formatSectionName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// Determine the best rendering strategy
type RenderStrategy =
  | "container-table"
  | "url-list"
  | "key-value-table"
  | "simple-list"
  | "generic";

function determineRenderStrategy(
  sectionName: string,
  items: Record<string, unknown>[],
): RenderStrategy {
  if (!items || items.length === 0) return "generic";

  const firstItem = items[0];
  const keys = Object.keys(firstItem);
  const lowerName = sectionName.toLowerCase();

  // Container-like data (has hostname, image, version fields)
  if (
    lowerName.includes("container") ||
    (keys.includes("hostname") && keys.includes("image")) ||
    (keys.includes("host") && keys.includes("image"))
  ) {
    return "container-table";
  }

  // URL-based data (liquibase, links, merge requests)
  if (
    lowerName.includes("liquibase") ||
    lowerName.includes("link") ||
    lowerName.includes("merge") ||
    keys.includes("url") ||
    keys.some((k) => firstItem[k]?.toString().startsWith("http"))
  ) {
    return "url-list";
  }

  // Simple text items
  if (
    keys.length === 1 ||
    (keys.length === 2 && keys.includes("id")) ||
    keys.includes("text") ||
    keys.includes("description") ||
    keys.includes("message")
  ) {
    return "simple-list";
  }

  // Key-value pairs (task-like data)
  if (
    lowerName.includes("task") ||
    lowerName.includes("config") ||
    keys.length <= 4
  ) {
    return "key-value-table";
  }

  return "generic";
}

// Container table renderer
function ContainerTable({ items }: { items: Record<string, unknown>[] }) {
  // Extract column headers from first item
  const allKeys = new Set<string>();
  items.forEach((item) => Object.keys(item).forEach((k) => allKeys.add(k)));

  // Prioritize certain columns
  const priorityOrder = [
    "hostname",
    "host",
    "name",
    "image",
    "version",
    "tag",
    "registry",
  ];
  const sortedKeys = Array.from(allKeys)
    .filter((k) => k !== "id" && !k.endsWith("_id"))
    .sort((a, b) => {
      const aIdx = priorityOrder.indexOf(a.toLowerCase());
      const bIdx = priorityOrder.indexOf(b.toLowerCase());
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.localeCompare(b);
    });

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {sortedKeys.map((key) => (
              <th
                key={key}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {formatSectionName(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item, idx) => (
            <tr key={idx} className="hover:bg-gray-50">
              {sortedKeys.map((key) => (
                <td
                  key={key}
                  className="px-4 py-3 text-sm text-gray-700 font-mono break-all max-w-xs"
                >
                  {renderCellValue(item[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// URL list renderer
function UrlList({ items }: { items: Record<string, unknown>[] }) {
  return (
    <div className="space-y-3">
      {items.map((item, idx) => {
        const url = findUrl(item);
        const description = findDescription(item);
        const otherFields = Object.entries(item).filter(
          ([k, v]) =>
            !isUrl(String(v)) &&
            k !== "id" &&
            !k.endsWith("_id") &&
            k !== "description" &&
            k !== "text",
        );

        return (
          <div
            key={idx}
            className="p-3 bg-gray-50 rounded-lg border border-gray-200"
          >
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 hover:underline font-medium break-all"
              >
                {url}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            ) : null}
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
            {otherFields.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {otherFields.map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-center px-2 py-1 rounded-md bg-gray-200 text-xs text-gray-700"
                  >
                    <span className="font-medium mr-1">
                      {formatSectionName(k)}:
                    </span>
                    {String(v)}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Key-value table renderer
function KeyValueTable({ items }: { items: Record<string, unknown>[] }) {
  return (
    <div className="space-y-3">
      {items.map((item, idx) => {
        const entries = Object.entries(item).filter(
          ([k]) => k !== "id" && !k.endsWith("_id"),
        );

        // If there's a URL, make it the primary display
        const urlEntry = entries.find(([, v]) => isUrl(String(v)));
        const otherEntries = entries.filter(([, v]) => !isUrl(String(v)));

        return (
          <div
            key={idx}
            className="p-3 bg-gray-50 rounded-lg border border-gray-200"
          >
            {urlEntry && (
              <a
                href={String(urlEntry[1])}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 hover:underline text-sm break-all"
              >
                {String(urlEntry[1])}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            )}
            {otherEntries.length > 0 && (
              <div className={`flex flex-wrap gap-2 ${urlEntry ? "mt-2" : ""}`}>
                {otherEntries.map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-center px-2 py-1 rounded-md bg-gray-200 text-xs text-gray-700"
                  >
                    <span className="font-medium mr-1">
                      {formatSectionName(k)}:
                    </span>
                    {String(v)}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Simple list renderer
function SimpleList({ items }: { items: Record<string, unknown>[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item, idx) => {
        const text = findTextContent(item);
        const url = findUrl(item);

        return (
          <li
            key={idx}
            className="flex items-start gap-2 p-2 bg-gray-50 rounded border-l-2 border-brand-500"
          >
            <span className="text-brand-600 mt-0.5">•</span>
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:underline break-all"
              >
                {text || url}
              </a>
            ) : (
              <span className="text-gray-700">{text}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// Generic fallback renderer
function GenericRenderer({ items }: { items: Record<string, unknown>[] }) {
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div
          key={idx}
          className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm"
        >
          {Object.entries(item)
            .filter(([k]) => k !== "id" && !k.endsWith("_id"))
            .map(([k, v]) => (
              <div key={k} className="flex gap-2 py-1">
                <span className="font-medium text-gray-600 min-w-[100px]">
                  {formatSectionName(k)}:
                </span>
                <span className="text-gray-800 break-all">
                  {renderCellValue(v)}
                </span>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

// Helper functions
function isUrl(value: string): boolean {
  return (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("git@")
  );
}

function findUrl(item: Record<string, unknown>): string | null {
  for (const [, value] of Object.entries(item)) {
    if (typeof value === "string" && isUrl(value)) {
      return value;
    }
  }
  return null;
}

function findDescription(item: Record<string, unknown>): string | null {
  const descKeys = ["description", "text", "message", "summary", "note"];
  for (const key of descKeys) {
    if (item[key] && typeof item[key] === "string") {
      return item[key] as string;
    }
  }
  return null;
}

function findTextContent(item: Record<string, unknown>): string {
  const textKeys = [
    "text",
    "description",
    "message",
    "content",
    "name",
    "title",
    "value",
  ];
  for (const key of textKeys) {
    if (item[key] && typeof item[key] === "string") {
      return item[key] as string;
    }
  }
  // Fallback: join all non-id string values
  return Object.entries(item)
    .filter(
      ([k, v]) => typeof v === "string" && k !== "id" && !k.endsWith("_id"),
    )
    .map(([, v]) => String(v))
    .join(" - ");
}

function renderCellValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-gray-400">-</span>;
  }

  const strValue = String(value);

  if (isUrl(strValue)) {
    return (
      <a
        href={strValue}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-600 hover:underline inline-flex items-center gap-1"
      >
        {truncateUrl(strValue)}
        <ExternalLink className="h-3 w-3 flex-shrink-0" />
      </a>
    );
  }

  return strValue;
}

function truncateUrl(url: string, maxLength: number = 50): string {
  if (url.length <= maxLength) return url;

  // Try to show the most relevant part
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname + urlObj.search;
    if (path.length > maxLength - 10) {
      return urlObj.hostname + "/..." + path.slice(-30);
    }
    return url.slice(0, maxLength - 3) + "...";
  } catch {
    return url.slice(0, maxLength - 3) + "...";
  }
}
